import Component, { Topic } from 'component';
import { assert, toNum } from 'utils';

export const MEDIA_ATTR = 'media-index';
export const VIDEO_ATTR = 'media-type-video';
export const VIDEO_HOST_ATTR = 'video-host';
export const THUMB_IMAGE_INDEX_ATTR = 'thumb-image-index';
export const CURRENT_INDEX_ATTR = 'current-index';
export const CURRENT_IMAGE_INDEX_ATTR = 'current-image-index';
export const THUMBS_ASIDE_ATTR = 'thumbs-aside';
export const VIDEO_AUTOPLAY_ATTR = 'video-autoplay';

customElements.define('x-product-gallery', class extends Component {
    constructor() {
        super();
        this.cacheElements();
    }

    currentIndex = null;
    currentImageIndex = null;
    modal = null;
    modalId;
    mediaList;
    mobileMediaItemWidth = 0;
    scrollSnapSupport = false;
    ignoreMobileIntersections = false;
    observer = null;
    _rubikHandler = null;
    
    // 关键：Rubik 处理期间锁定所有操作
    _rubikLock = false;

    cacheElements() {
        this.$element.containerRoot = this.querySelector('[data-product-gallery-element="container-root"]');
        this.$element.track = this.querySelector('[data-product-gallery-element="track"]');
        this.$element.modalImages = this.querySelectorAll('[data-product-gallery-element="modal-images"]');
        this.$element.thumbsCheck = this.querySelectorAll('[data-product-gallery-element="thumbs-check"]');
        this.$element.prevButton = this.querySelector('[data-product-gallery-element="arrow-prev"]');
        this.$element.nextButton = this.querySelector('[data-product-gallery-element="arrow-next"]');
    }

    render() {
        this.watchViewport = true;
        assert(Boolean(this.$element.track?.childElementCount), "Product doesn't have any medias");
        
        this.modalId = this.getAttribute('modal-id');
        this.currentImageIndex = toNum(this.getAttribute(CURRENT_IMAGE_INDEX_ATTR));
        this.currentIndex = toNum(this.getAttribute(CURRENT_INDEX_ATTR)) || 0;

        this.initModal();
        this.initVideoControl();
        this.initArrows();
        this.initRubikListener();

        this.on(this, 'change', (e) => {
            // Rubik 处理中，忽略 change 事件
            if (this._rubikLock) return;
            
            e.stopPropagation();
            const target = e.target;
            const activeMediaIndex = toNum(target.value);
            if (activeMediaIndex !== null) {
                this.setCurrentIndex(activeMediaIndex);
                if (this.mobileQuery?.matches) {
                    this.scrollToActiveMedia(activeMediaIndex);
                }
            }
            const targetImageIndex = toNum(target.getAttribute(THUMB_IMAGE_INDEX_ATTR));
            if (targetImageIndex !== null) {
                this.setCurrentImageIndex(targetImageIndex);
            }
        });

        this.sub(Topic.PRODUCT_GALLERY_UPDATE, (currentIndex) => {
            // Rubik 处理中，忽略外部更新
            if (this._rubikLock) return;
            
            if (this.setCurrentIndex(currentIndex)) {
                this.setCurrentThumb();
            }
            if (this.mobileQuery?.matches) {
                this.scrollToActiveMedia(currentIndex);
            }
        });

        requestAnimationFrame(() => {
            this.updateArrowsState();
        });
    }

    // ==================== Rubik 插件处理 ====================

    initRubikListener() {
        this._rubikHandler = () => {
            // 立即锁定，阻止所有其他操作
            this._rubikLock = true;
            
            // 立即停止当前滚动
            const track = this.querySelector('[data-product-gallery-element="track"]');
            if (track) {
                track.scrollTo({ left: 0, behavior: 'instant' });
            }

            this.waitForRubikRender().then(() => {
                this.resetToFirstSlide();
                // 延迟解锁，确保所有后续事件都被阻止
                setTimeout(() => {
                    this._rubikLock = false;
                }, 100);
            });
        };
        document.addEventListener('rubik-swatch-clicked', this._rubikHandler);
    }

    waitForRubikRender() {
        return new Promise((resolve) => {
            let resolved = false;
            let mutationTimeout;

            const tempObserver = new MutationObserver(() => {
                clearTimeout(mutationTimeout);
                mutationTimeout = setTimeout(() => {
                    if (!resolved) {
                        resolved = true;
                        tempObserver.disconnect();
                        resolve();
                    }
                }, 150);
            });

            const track = this.querySelector('[data-product-gallery-element="track"]');
            if (track) {
                tempObserver.observe(track, {
                    childList: true,
                    subtree: true,
                    attributes: true
                });
            }

            // 保底超时
            setTimeout(() => {
                if (!resolved) {
                    resolved = true;
                    tempObserver.disconnect();
                    resolve();
                }
            }, 600);
        });
    }

    resetToFirstSlide() {
        // 重新缓存元素
        this.cacheElements();

        // 重新绑定箭头事件
        this.rebindArrowEvents();

        // 重置状态到第一张
        this.currentIndex = 0;
        this.currentImageIndex = 0;
        this.style.setProperty('--product-gallery-current-index', '0');
        this.setAttribute(CURRENT_INDEX_ATTR, '0');

        // 设置第一个缩略图为选中
        const firstThumb = this.querySelector('[data-product-gallery-element="thumbs-check"]');
        if (firstThumb) {
            firstThumb.checked = true;
            const imageIndex = toNum(firstThumb.getAttribute(THUMB_IMAGE_INDEX_ATTR));
            if (imageIndex !== null) {
                this.currentImageIndex = imageIndex;
            }
        }

        // 强制滚动到第一张（使用 instant 避免动画）
        const track = this.querySelector('[data-product-gallery-element="track"]');
        if (track) {
            track.scrollTo({ left: 0, behavior: 'instant' });
        }

        // 更新箭头状态
        this.updateArrowsState();

        // 重新初始化视频观察器
        this.reinitVideoObserver();
    }

    rebindArrowEvents() {
        const prevBtn = this.querySelector('[data-product-gallery-element="arrow-prev"]');
        const nextBtn = this.querySelector('[data-product-gallery-element="arrow-next"]');

        if (prevBtn) {
            const newPrev = prevBtn.cloneNode(true);
            prevBtn.parentNode?.replaceChild(newPrev, prevBtn);
            this.$element.prevButton = newPrev;
            newPrev.addEventListener('click', (e) => {
                e.preventDefault();
                this.navigateGallery(-1);
            });
        }

        if (nextBtn) {
            const newNext = nextBtn.cloneNode(true);
            nextBtn.parentNode?.replaceChild(newNext, nextBtn);
            this.$element.nextButton = newNext;
            newNext.addEventListener('click', (e) => {
                e.preventDefault();
                this.navigateGallery(1);
            });
        }
    }

    reinitVideoObserver() {
        if (this.observer) {
            this.observer.disconnect();
        }
        const videos = this.querySelectorAll(`[${VIDEO_ATTR}]`);
        if (videos.length > 0) {
            this.observer = this.createObserver();
            videos.forEach(video => this.observer.observe(video));
        }
    }

    // ==================== 箭头导航 ====================

    initArrows() {
        if (this.$element.prevButton) {
            this.on(this.$element.prevButton, 'click', (e) => {
                e.preventDefault();
                this.navigateGallery(-1);
            });
        }
        if (this.$element.nextButton) {
            this.on(this.$element.nextButton, 'click', (e) => {
                e.preventDefault();
                this.navigateGallery(1);
            });
        }
    }

    navigateGallery(direction) {
        // Rubik 处理中不响应导航
        if (this._rubikLock) return;
        
        const medias = this.querySelectorAll(`[${MEDIA_ATTR}]`);
        const count = medias.length;
        
        if (count <= 1) return;

        const current = this.currentIndex || 0;
        let newIndex = current + direction;

        if (newIndex < 0) newIndex = 0;
        if (newIndex > count - 1) newIndex = count - 1;
        if (newIndex === current) return;

        const thumbInput = this.querySelector(`input[data-product-gallery-element="thumbs-check"][value="${newIndex}"]`);
        
        if (thumbInput) {
            thumbInput.click();
        } else {
            if (this.setCurrentIndex(newIndex)) {
                this.setCurrentThumb();
            }
            if (this.mobileQuery?.matches) {
                this.scrollToActiveMedia(newIndex);
            }
        }
    }

    updateArrowsState() {
        const prevButton = this.querySelector('[data-product-gallery-element="arrow-prev"]');
        const nextButton = this.querySelector('[data-product-gallery-element="arrow-next"]');

        if (!prevButton || !nextButton) return;

        const medias = this.querySelectorAll(`[${MEDIA_ATTR}]`);
        const count = medias.length;
        const current = this.currentIndex || 0;

        if (count <= 1) {
            prevButton.setAttribute('disabled', '');
            nextButton.setAttribute('disabled', '');
            return;
        }

        if (current <= 0) {
            prevButton.setAttribute('disabled', '');
        } else {
            prevButton.removeAttribute('disabled');
        }

        if (current >= count - 1) {
            nextButton.setAttribute('disabled', '');
        } else {
            nextButton.removeAttribute('disabled');
        }
    }

    // ==================== 移动端处理 ====================

    onMobile() {
        this.mediaList = this.querySelectorAll(`[${MEDIA_ATTR}]`);
        this.mobileMediaItemWidth = this.mediaList.item(0)?.clientWidth || 0;
        this.scrollSnapSupport = 'onscrollsnapchange' in window;
        this.currentIndex = toNum(this.getAttribute(CURRENT_INDEX_ATTR));
        
        if (this.currentIndex !== null && !this._rubikLock) {
            this.scrollToActiveMedia(this.currentIndex);
        }
        
        if (this.scrollSnapSupport) {
            this.on(this.$element.track, 'scrollsnapchange', this.handleSnapChange);
        } else {
            if (!this.observer) {
                this.observer = this.createObserver();
            }
            this.mediaList.forEach(media => this.observer.observe(media));
        }
    }

    createObserver() {
        return new IntersectionObserver(entries => {
            // Rubik 处理中忽略
            if (this._rubikLock) return;
            
            entries.forEach(entry => {
                if (this.mobileQuery?.matches) {
                    if (entry.isIntersecting && !this.ignoreMobileIntersections) {
                        const target = entry.target;
                        const activeIndex = toNum(target.getAttribute(MEDIA_ATTR));
                        if (activeIndex !== null && this.setCurrentIndex(activeIndex)) {
                            this.setCurrentThumb();
                            this.updateArrowsState();
                        }
                    }
                }
                if (entry.target.hasAttribute(VIDEO_ATTR)) {
                    const video = entry.target.firstElementChild;
                    const videoHost = entry.target.getAttribute(VIDEO_HOST_ATTR);
                    const toggleState = entry.isIntersecting && this.videoAutoplay;
                    if (video?.tagName === 'VIDEO') {
                        this.toggleHTMLVideoState(video, toggleState);
                    } else if (video?.tagName === 'IFRAME') {
                        this.toggleExternalVideoState(video, videoHost, toggleState);
                    }
                }
            });
        }, { root: this.$element.containerRoot, threshold: 0.9 });
    }

    scrollToActiveMedia(index) {
        // Rubik 处理中不滚动
        if (this._rubikLock) return;
        
        if (!this.scrollSnapSupport) {
            this.ignoreMobileIntersections = true;
        }
        this.$element.track?.scrollTo({
            left: this.mobileMediaItemWidth * index,
            behavior: 'smooth'
        });
        if (!this.scrollSnapSupport) {
            setTimeout(() => {
                this.ignoreMobileIntersections = false;
            }, 1000);
        }
    }

    handleSnapChange(e) {
        // Rubik 处理中忽略
        if (this._rubikLock) return;
        
        const target = e.snapTargetInline;
        const activeIndex = toNum(target.getAttribute(MEDIA_ATTR));
        if (activeIndex !== null && this.setCurrentIndex(activeIndex)) {
            this.setCurrentThumb();
            this.updateArrowsState();
        }
    }

    // ==================== 模态框 ====================

    initModal() {
        customElements.whenDefined('x-modal').then(() => {
            if (this.modalId) {
                this.modal = document.getElementById(this.modalId);
                if (this.modal) {
                    this.modal.beforeOpen = this.scrollToModalImage.bind(this);
                }
            }
        });
    }

    scrollToModalImage() {
        if (this.currentImageIndex !== undefined && this.currentImageIndex !== null) {
            const target = this.$element.modalImages?.item(this.currentImageIndex);
            if (target) {
                target.scrollIntoView({ block: 'center' });
            }
        }
    }

    // ==================== 视频控制 ====================

    initVideoControl() {
        const videos = this.querySelectorAll(`[${VIDEO_ATTR}]`);
        if (videos.length > 0) {
            if (!this.observer) {
                this.observer = this.createObserver();
            }
            videos.forEach(video => this.observer.observe(video));
        }
    }

    toggleHTMLVideoState(video, play = false) {
        play ? video.play().catch(() => {}) : video.pause();
    }

    toggleExternalVideoState(embeddedVideo, videoHost, play = false) {
        const videoFrame = embeddedVideo.contentWindow;
        if (!videoFrame) return;
        const action = play ? 'play' : 'pause';
        if (videoHost === 'youtube') {
            videoFrame.postMessage(JSON.stringify({ event: 'command', func: `${action}Video`, args: '' }), '*');
        } else if (videoHost === 'vimeo') {
            videoFrame.postMessage({ method: action }, '*');
        }
    }

    // ==================== 状态管理 ====================

    setCurrentThumb() {
        if (this.currentIndex === undefined || this.currentIndex === null) return;

        const thumbsCheck = this.querySelectorAll('[data-product-gallery-element="thumbs-check"]');
        const currentThumb = thumbsCheck[this.currentIndex];
        
        if (currentThumb) {
            currentThumb.checked = true;
            if (this.thumbsAside && !this.mobileQuery?.matches) {
                currentThumb.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
            }
            const currentThumbImageIndex = toNum(currentThumb.getAttribute(THUMB_IMAGE_INDEX_ATTR));
            if (currentThumbImageIndex !== null) {
                this.setCurrentImageIndex(currentThumbImageIndex);
            }
        }
    }

    setCurrentIndex(currentIndex) {
        // Rubik 处理中不允许修改
        if (this._rubikLock) return false;
        
        if (currentIndex === this.currentIndex) return false;
        
        this.style.setProperty('--product-gallery-current-index', currentIndex.toString());
        this.setAttribute(CURRENT_INDEX_ATTR, currentIndex.toString());
        this.currentIndex = currentIndex;
        this.updateArrowsState();
        
        return true;
    }

    setCurrentImageIndex(currentImageIndex) {
        if (currentImageIndex === this.currentImageIndex) return false;
        this.currentImageIndex = currentImageIndex;
        return true;
    }

    get thumbsAside() {
        return this.hasAttribute(THUMBS_ASIDE_ATTR);
    }

    get videoAutoplay() {
        return this.hasAttribute(VIDEO_AUTOPLAY_ATTR);
    }

    // ==================== 清理 ====================

    disconnectedCallback() {
        super.disconnectedCallback?.();
        if (this.observer) {
            this.observer.disconnect();
            this.observer = null;
        }
        if (this._rubikHandler) {
            document.removeEventListener('rubik-swatch-clicked', this._rubikHandler);
            this._rubikHandler = null;
        }
    }
});